/**
 * Tabellen inlezen: CSV/TXT en Excel → rijen → geplande punten.
 *
 * Een meetstaat ziet er bij elk bureau anders uit: puntkomma of komma, wel of geen kopregel,
 * 'X'/'Y' of 'RD-X'/'Oost', soms eerst een paar regels met projectinformatie. Daarom raden we
 * hier zo veel mogelijk (scheidingsteken, kopregel, kolommen, stelsel) en laat de importdialoog
 * de gebruiker elke gok corrigeren vóór er iets wordt opgeslagen.
 */
import { unzipSync, strFromU8 } from 'fflate';
import {
  guessCoordinateSystem, pointToWgs84, parseNumber, parseDepthCm, normalizeKind,
} from '@mv/models';

// ── CSV ──────────────────────────────────────────────────────────────────────

/** Scheidingsteken: degene die op de meeste regels hetzelfde (en >0) aantal kolommen geeft. */
export function detectDelimiter(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim()).slice(0, 30);
  let best = ',', bestScore = -1;
  for (const d of [';', '\t', ',', '|']) {
    const counts = lines.map(l => splitCsvLine(l, d).length);
    const freq = new Map();
    for (const c of counts) if (c > 1) freq.set(c, (freq.get(c) || 0) + 1);
    const score = Math.max(0, ...freq.values());
    if (score > bestScore) { best = d; bestScore = score; }
  }
  return best;
}

/** Eén CSV-regel splitsen, met aanhalingstekens ("a;b" blijft één veld, "" = "). */
export function splitCsvLine(line, delimiter) {
  const out = [];
  let cur = '', quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') quoted = false;
      else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === delimiter) { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out.map(s => s.trim());
}

export function parseCsv(text) {
  const clean = text.replace(/^﻿/, '');
  const delimiter = detectDelimiter(clean);
  const rows = clean.split(/\r?\n/).filter(l => l.trim()).map(l => splitCsvLine(l, delimiter));
  return { rows, delimiter };
}

/**
 * Tekst uit een bestand, met een terugval naar Windows-1252 als het geen geldige UTF-8 is.
 * Excel bewaart 'CSV (gescheiden door lijstscheidingsteken)' nog altijd zo, en dan wordt
 * 'Boring één' anders 'Boring Ã©Ã©n'.
 */
export function decodeText(buf) {
  try { return new TextDecoder('utf-8', { fatal: true }).decode(buf); }
  catch (_) { return new TextDecoder('windows-1252').decode(buf); }
}

// ── Excel (.xlsx/.xlsm) ──────────────────────────────────────────────────────

const xmlDecode = (s) => s
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'")
  .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n))).replace(/&amp;/g, '&');

function colIndex(ref) {
  const letters = ref.replace(/\d+$/, '');
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/**
 * Minimale xlsx-lezer: alle bladen als 2D-array met celwaarden (tekst of getal). Geen opmaak,
 * geen formules -- de laatst berekende waarde staat in het bestand en die is wat we nodig hebben.
 * Een hele bibliotheek (SheetJS ~1 MB) voor twee kolommen getallen is te veel.
 *
 * @param {Uint8Array} bytes
 * @returns {{ name: string, rows: (string|number)[][] }[]}
 */
export function readXlsx(bytes) {
  const files = unzipSync(bytes, { filter: f => f.name.startsWith('xl/') && f.name.endsWith('.xml') || f.name.endsWith('.rels') });
  const text = (name) => (files[name] ? strFromU8(files[name]) : '');
  const shared = [...text('xl/sharedStrings.xml').matchAll(/<si>([\s\S]*?)<\/si>/g)]
    .map(m => xmlDecode([...m[1].matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map(t => t[1]).join('')));
  const rels = text('xl/_rels/workbook.xml.rels');
  const target = {};
  for (const m of rels.matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const id = (m[1].match(/\bId="([^"]+)"/) || [])[1];
    const t = (m[1].match(/\bTarget="([^"]+)"/) || [])[1];
    if (id && t) target[id] = t.replace(/^\/?xl\//, '');
  }
  const sheets = [];
  for (const m of text('xl/workbook.xml').matchAll(/<sheet\b([^>]*)\/>/g)) {
    const name = xmlDecode((m[1].match(/\bname="([^"]*)"/) || [])[1] || '');
    const rid = (m[1].match(/\br:id="([^"]+)"/) || [])[1];
    const xml = text('xl/' + (target[rid] || ''));
    if (!xml) continue;
    const rows = [];
    for (const r of xml.matchAll(/<row\b[^>]*?\br="(\d+)"[^>]*>([\s\S]*?)<\/row>/g)) {
      const row = [];
      for (const c of r[2].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const ref = (c[1].match(/\br="([A-Z]+\d+)"/) || [])[1];
        if (!ref) continue;
        const inner = c[2] || '';
        const type = (c[1].match(/\bt="([^"]+)"/) || [])[1];
        const v = (inner.match(/<v>([\s\S]*?)<\/v>/) || [])[1];
        let val = '';
        if (type === 's' && v != null) val = shared[Number(v)] ?? '';
        else if (type === 'inlineStr') val = xmlDecode((inner.match(/<t[^>]*>([\s\S]*?)<\/t>/) || [])[1] || '');
        else if (type === 'str' || type === 'b' || type === 'e') val = v != null ? xmlDecode(v) : '';
        else if (v != null) val = Number(v);
        row[colIndex(ref)] = val;
      }
      rows[Number(r[1]) - 1] = Array.from(row, x => x ?? '');
    }
    sheets.push({ name, rows: Array.from(rows, x => x ?? []) });
  }
  return sheets;
}

// ── Kolommen herkennen ───────────────────────────────────────────────────────

const HEADER_PATTERNS = {
  name: /^(naam|name|punt|puntnr|punt ?nummer|meetpunt|boring|boornr|boring ?nr|nr|id|code|label|locatie)\b/i,
  x: /^(x|rd[ _-]?x|x[ _-]?rd|x[ _-]?co[oö]rd|oost|easting|east|e|lon|lng|long|lengtegraad)\b/i,
  y: /^(y|rd[ _-]?y|y[ _-]?rd|y[ _-]?co[oö]rd|noord|northing|north|n|lat|breedtegraad)\b/i,
  depth: /(diepte|depth|einddiepte|boordiepte|m-?mv)/i,
  kind: /^(soort|type|kind|categorie|object)\b/i,
  note: /^(opmerking|omschrijving|toelichting|note|notes|remark|beschrijving)\b/i,
};

/**
 * Zoek de kopregel en de kolommen. Een kopregel is de eerste regel (van de eerste 15) waarin
 * een x- én y-kolom te herkennen zijn. Geen kopregel → kolommen raden aan de inhoud: de eerste
 * tekstkolom is de naam, de eerste twee getalkolommen zijn x en y.
 *
 * @returns {{ headerRow: number, mapping: { name:number, x:number, y:number, depth:number, kind:number, note:number } }}
 */
export function guessColumns(rows) {
  for (let r = 0; r < Math.min(rows.length, 15); r++) {
    const cells = (rows[r] || []).map(c => String(c ?? '').trim());
    const find = (re, skip = []) => cells.findIndex((c, i) => c && re.test(c) && !skip.includes(i));
    const x = find(HEADER_PATTERNS.x);
    const y = find(HEADER_PATTERNS.y, [x]);
    if (x >= 0 && y >= 0) {
      return {
        headerRow: r,
        mapping: {
          name: find(HEADER_PATTERNS.name, [x, y]),
          x, y,
          depth: find(HEADER_PATTERNS.depth, [x, y]),
          kind: find(HEADER_PATTERNS.kind, [x, y]),
          note: find(HEADER_PATTERNS.note, [x, y]),
        },
      };
    }
  }
  // Geen kopregel: aan de inhoud raden, op de eerste regel die minstens twee getallen heeft.
  const start = rows.findIndex(r => (r || []).filter(c => Number.isFinite(parseNumber(c))).length >= 2);
  const sample = rows[start] || [];
  const numeric = sample.map((c, i) => (Number.isFinite(parseNumber(c)) ? i : -1)).filter(i => i >= 0);
  const textual = sample.map((c, i) => (String(c ?? '').trim() && !Number.isFinite(parseNumber(c)) ? i : -1)).filter(i => i >= 0);
  // Een puntnaam als '101' is een getal; zijn er drie getalkolommen en geen tekst, dan is de eerste de naam.
  const nameCol = textual.length ? textual[0] : (numeric.length >= 3 ? numeric.shift() : -1);
  return {
    headerRow: start - 1,
    mapping: { name: nameCol, x: numeric[0] ?? -1, y: numeric[1] ?? -1, depth: numeric[2] ?? -1, kind: -1, note: -1 },
  };
}

/**
 * Rijen → ruwe punten (nog in het stelsel van het bestand). Rijen zonder twee geldige getallen
 * worden overgeslagen (een tussenkop, een lege regel, een totaalregel).
 */
export function rowsToRawPoints(rows, headerRow, mapping) {
  const out = [];
  for (let r = headerRow + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const x = parseNumber(row[mapping.x]);
    const y = parseNumber(row[mapping.y]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const name = mapping.name >= 0 ? String(row[mapping.name] ?? '').trim() : '';
    out.push({
      row: r + 1,
      name: name || `P${out.length + 1}`,
      x, y,
      plannedDepthCm: mapping.depth >= 0 ? parseDepthCm(row[mapping.depth]) : null,
      kind: normalizeKind(mapping.kind >= 0 ? row[mapping.kind] : null, name),
      note: mapping.note >= 0 ? (String(row[mapping.note] ?? '').trim() || null) : null,
    });
  }
  return out;
}

/** Ruwe punten → WGS84. Punten die niet om te rekenen zijn krijgen `error`. */
export function rawPointsToWgs84(raw, system, swapped) {
  return raw.map(p => {
    const w = pointToWgs84(p.x, p.y, system, swapped);
    return w ? { ...p, sourceX: p.x, sourceY: p.y, x: w.x, y: w.y } : { ...p, error: 'niet om te rekenen' };
  });
}

/** Gok het stelsel voor een set ruwe punten (zie @mv/models guessCoordinateSystem). */
export function guessSystemFor(raw) {
  return guessCoordinateSystem(raw.map(p => [p.x, p.y]));
}
